<template>
  <div class="flex flex-col gap-2">
    <div class="flex flex-wrap items-center gap-2">
      <form class="contents" @submit.prevent="submitSearch">
        <input v-model="search" type="search" class="input w-60" aria-label="Search PO number" placeholder="Search PO number…" />
      </form>
      <MultiFilterSelect :model-value="filters.vendors" label="Vendors" :options="vendors" @update:model-value="patch({ vendors: $event })" />
      <MultiFilterSelect :model-value="filters.sources" label="Source" :options="SOURCE_OPTIONS" @update:model-value="patch({ sources: $event as PoActivitySource[] })" />
      <MultiFilterSelect :model-value="filters.actions" label="Actions" :options="ACTION_OPTIONS" @update:model-value="patch({ actions: $event as PoActivityAction[] })" />
      <MultiFilterSelect :model-value="filters.actors" label="People" :options="actors" @update:model-value="patch({ actors: $event })" />
      <MultiFilterSelect v-if="customers.length > 0" :model-value="filters.customers" label="Customers" :options="customers" @update:model-value="patch({ customers: $event })" />
      <FilterSelect :model-value="filters.range" :options="RANGE_OPTIONS" @update:model-value="patch({ range: $event as ActivityRange })" />
      <span v-if="total !== null" class="ml-auto text-[13px] tabular-nums text-gray-500">{{ total.toLocaleString('en-US') }} {{ total === 1 ? 'change' : 'changes' }}</span>
    </div>

    <!-- Every active filter as a pill, so what is narrowing the feed is readable without
         opening any dropdown, and removable one at a time. -->
    <div v-if="pills.length > 0" class="flex flex-wrap items-center gap-1.5">
      <span v-for="pill in pills" :key="pill.key" class="inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 py-0.5 pl-2.5 pr-1.5 text-xs font-medium text-blue-800">
        <span class="text-blue-500">{{ pill.kind }}</span>
        {{ pill.label }}
        <button type="button" class="rounded-full px-1 leading-none text-blue-500 hover:bg-blue-100 hover:text-blue-800" :aria-label="`Remove ${pill.kind} ${pill.label}`" @click="emit('update', pill.without())">×</button>
      </span>
      <button type="button" class="ml-1 text-xs font-medium text-gray-500 hover:text-gray-800 hover:underline" @click="emit('update', EMPTY_FILTERS)">Clear filters</button>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue';

  import FilterSelect from '@/components/common/FilterSelect.vue';
  import MultiFilterSelect from '@/components/activity/MultiFilterSelect.vue';
  import type { PoActivityAction, PoActivitySource } from '@/types/poActivity';
  import { PO_ACTIVITY_ACTIONS, PO_ACTIVITY_SOURCES } from '@/types/poActivity';
  import type { ActivityFilters, ActivityRange } from '@/utils/poActivity';
  import { ACTIVITY_RANGES, EMPTY_FILTERS } from '@/utils/poActivity';

  interface Pill {
    key: string;
    kind: string;
    label: string;
    without: () => ActivityFilters;
  }

  const props = defineProps<{
    filters: ActivityFilters;
    vendors: { value: string; label: string }[];
    actors: { value: string; label: string }[];
    // Empty hides the control — the caller passes nothing in privacy mode, where customer names
    // must not show on a shared screen.
    customers: { value: string; label: string }[];
    total: number | null;
  }>();
  const emit = defineEmits<{ update: [filters: ActivityFilters] }>();

  const ACTION_LABELS: Record<PoActivityAction, string> = {
    scheduled: 'Scheduled',
    moved: 'Moved',
    quantity_changed: 'Quantity',
    removed: 'Removed',
    note_changed: 'Note',
    marked_scheduled: 'Marked scheduled',
    unmarked_scheduled: 'Unmarked scheduled',
    shipment_planned: 'Shipment planned',
    shipment_moved: 'Shipment moved',
    shipment_lines_changed: 'Shipment lines changed',
    shipment_note_changed: 'Shipment note changed',
    shipment_removed: 'Shipment removed',
    shipped: 'Shipped',
    hawb_added: 'HAWB/BOL added',
    packing_list_added: 'Packing list added',
    delivery_requested: 'Delivery requested',
    delivery_request_withdrawn: 'Delivery request withdrawn',
    delivery_request_answered: 'Delivery request answered',
  };
  const SOURCE_LABELS: Record<PoActivitySource, string> = { board: 'Scheduling board', tracker: 'Tracker' };
  const RANGE_LABELS: Record<ActivityRange, string> = {
    '7d': 'Last 7 days',
    '30d': 'Last 30 days',
    '90d': 'Last 90 days',
    all: 'All time',
  };
  const ACTION_OPTIONS = PO_ACTIVITY_ACTIONS.map((action) => ({ value: action, label: ACTION_LABELS[action] }));
  const SOURCE_OPTIONS = PO_ACTIVITY_SOURCES.map((source) => ({ value: source, label: SOURCE_LABELS[source] }));
  const RANGE_OPTIONS = ACTIVITY_RANGES.map((range) => ({ value: range, label: RANGE_LABELS[range] }));

  const search = ref('');
  watch(
    () => props.filters.po,
    (po) => {
      if (!po) search.value = '';
    },
  );

  const labelOf = (options: { value: string; label: string }[], value: string): string => options.find((option) => option.value === value)?.label ?? value;

  function listPills<K extends 'vendors' | 'actions' | 'sources' | 'actors' | 'customers'>(field: K, kind: string, options: { value: string; label: string }[]): Pill[] {
    return props.filters[field].map((value) => ({
      key: `${field}:${value}`,
      kind,
      label: labelOf(options, value),
      without: () => ({ ...props.filters, [field]: props.filters[field].filter((item) => item !== value) }),
    }));
  }

  const pills = computed<Pill[]>(() => {
    const { filters } = props;
    const subject: Pill[] = filters.po
      ? [{ key: 'po', kind: 'PO', label: filters.po, without: () => ({ ...filters, po: null }) }]
      : filters.assignmentId
        ? // A reservation has no PO number to print, so the pill names what is being filtered.
          [{ key: 'assignment', kind: 'Reservation', label: 'selected block', without: () => ({ ...filters, assignmentId: null }) }]
        : [];
    const range: Pill[] =
      filters.range === EMPTY_FILTERS.range ? [] : [{ key: 'range', kind: 'Range', label: RANGE_LABELS[filters.range], without: () => ({ ...filters, range: EMPTY_FILTERS.range }) }];
    return [
      ...subject,
      ...listPills('vendors', 'Vendor', props.vendors),
      ...listPills('sources', 'Source', SOURCE_OPTIONS),
      ...listPills('actions', 'Action', ACTION_OPTIONS),
      ...listPills('actors', 'By', props.actors),
      ...listPills('customers', 'Customer', props.customers),
      ...range,
    ];
  });

  function patch(changes: Partial<ActivityFilters>): void {
    emit('update', { ...props.filters, ...changes });
  }

  function submitSearch(): void {
    const po = search.value.trim();
    if (po) patch({ po });
  }
</script>
